import React, { memo } from 'react';
import { motion } from 'framer-motion';
import { Sparkles, ArrowRight, RotateCcw, FastForward, Eye } from 'lucide-react';
import { cn } from '../../../lib/utils';

// ═══════════════════════════════════════════════════════════════════════════════
// SHOWCASE FRAME
//
// The chrome that says, unambiguously, "this is a product demo."
//
// A visitor reported reading the old showcase as the project owner's private
// chat history — a shared roadmap link that appeared to leak a conversation. So
// the framing is not decoration: the bar is always on screen, the intro card is
// the first thing in the transcript, and the outro closes the loop at the end.
// All three are static, so all three are memoized.
// ═══════════════════════════════════════════════════════════════════════════════

const controlClass =
    'inline-flex shrink-0 items-center gap-1.5 rounded-lg border border-white/10 bg-white/[0.04] ' +
    'px-2.5 py-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.14em] ' +
    'text-muted-foreground transition-colors hover:border-primary/40 hover:bg-white/[0.08] hover:text-primary';

/**
 * Persistent bar in the chat surface's header slot — the same row a signed-in
 * user's session title occupies, which is suppressed for shared links.
 */
export const ShowcaseDemoBar = memo(function ShowcaseDemoBar({ phase, onSkip, onReplay, className }) {
    return (
        <div
            data-testid="showcase-demo-bar"
            className={cn(
                'flex flex-shrink-0 items-center justify-between gap-3 border-b border-primary/20',
                'bg-primary/[0.06] px-4 py-2.5',
                className
            )}
        >
            <div className="flex min-w-0 items-center gap-2.5">
                <span className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-primary/40 bg-primary/20 px-2 py-0.5 font-mono text-[10px] font-bold uppercase tracking-[0.18em] text-primary">
                    <Sparkles className="h-3 w-3" />
                    Demo
                </span>
                <p className="min-w-0 truncate text-xs text-muted-foreground">
                    <span className="font-medium text-white">A scripted example of Forge Advisor.</span>
                    <span className="hidden sm:inline"> Not this project&apos;s conversations — those stay private.</span>
                </p>
            </div>

            {phase === 'playing' ? (
                <button type="button" onClick={onSkip} data-testid="showcase-skip" className={controlClass}>
                    <FastForward className="h-3 w-3" />
                    Skip
                </button>
            ) : (
                <button type="button" onClick={onReplay} data-testid="showcase-replay" className={controlClass}>
                    <RotateCcw className="h-3 w-3" />
                    Replay
                </button>
            )}
        </div>
    );
});

const INTRO_POINTS = [
    'Breaks a launch into prioritized tasks — and undoes any of them in a click',
    'Builds mockups in real React you can open, click through and edit',
    'Writes the docs, then shows you the diff before anything changes',
];

/**
 * First thing in the transcript, on screen before any message lands.
 *
 * Leads with what the product does, because that is what a visitor came for.
 * The "this is a demo" line is one clause inside the subhead rather than the
 * headline — enough that nobody mistakes it for the project's real chat (the
 * bug this replaced), not so much that the card spends its whole word count
 * apologising instead of selling.
 */
export const ShowcaseIntroCard = memo(function ShowcaseIntroCard({ reduceMotion = false }) {
    return (
        <motion.div
            data-testid="showcase-intro"
            initial={reduceMotion ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-2xl border border-primary/20 bg-gradient-to-b from-primary/[0.08] to-transparent p-5 md:p-6"
        >
            <div className="flex items-start gap-4">
                <div className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/30 to-accent/20">
                    <Sparkles className="h-5 w-5 text-primary" />
                </div>
                <div className="min-w-0">
                    <p className="mb-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.28em] text-primary/80">
                        Forge Advisor
                    </p>
                    <h2 className="mb-2 font-mono text-lg font-bold tracking-tight text-white md:text-xl">
                        Ask once. Get a plan, a mockup and a brief.
                    </h2>
                    <p className="mb-4 text-sm leading-relaxed text-muted-foreground">
                        Watch Forge Advisor take a launch from one sentence to shipped work — in a
                        single conversation, starting now. It&apos;s a scripted demo, not this
                        project&apos;s chat.
                    </p>

                    <ul className="mb-4 space-y-1.5">
                        {INTRO_POINTS.map((point) => (
                            <li key={point} className="flex items-start gap-2.5 text-sm text-foreground/75">
                                <ArrowRight className="mt-0.5 h-3.5 w-3.5 shrink-0 text-primary/70" />
                                {point}
                            </li>
                        ))}
                    </ul>

                    <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[11px] text-muted-foreground/70">
                        <span className="inline-flex items-center gap-1.5">
                            <Eye className="h-3 w-3" />
                            Every card below is live — open one and look around
                        </span>
                    </div>
                </div>
            </div>
        </motion.div>
    );
});

/** Closes the loop once playback finishes, and carries the CTA. */
export const ShowcaseOutroCard = memo(function ShowcaseOutroCard({ onReplay, onGetStarted, reduceMotion = false }) {
    return (
        <motion.div
            data-testid="showcase-outro"
            initial={reduceMotion ? false : { opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="rounded-2xl border border-white/10 bg-white/[0.03] p-5 md:p-6"
        >
            <p className="mb-1.5 font-mono text-[10px] font-bold uppercase tracking-[0.28em] text-muted-foreground/70">
                End of demo
            </p>
            <h3 className="mb-2 font-mono text-lg font-bold tracking-tight text-white">
                Now run it on your own project
            </h3>
            <p className="mb-5 max-w-xl text-sm leading-relaxed text-muted-foreground">
                That was one conversation: eight tasks planned and prioritized, a landing page built in
                real React, a brief written and revised. Forge Advisor does the same on whatever
                you&apos;re building. Free to start.
            </p>

            <div className="flex flex-wrap items-center gap-2.5">
                <button
                    type="button"
                    onClick={onGetStarted}
                    data-testid="showcase-outro-cta"
                    className="inline-flex items-center gap-2 rounded-xl bg-primary px-5 py-2.5 text-sm font-bold text-primary-foreground shadow-md shadow-primary/25 transition-all hover:bg-primary/90 hover:shadow-lg hover:shadow-primary/30"
                >
                    Start building free
                    <ArrowRight className="h-4 w-4" />
                </button>
                <button type="button" onClick={onReplay} className={controlClass}>
                    <RotateCcw className="h-3 w-3" />
                    Replay demo
                </button>
            </div>
        </motion.div>
    );
});
